import React from 'react'
import { useSysTheme } from 'hooks/useUiState'
import styled, { css } from 'styled-components'
import { getDefaultColor } from 'utils/color'
import { AutomationId, AUTO_ATTR_KEY } from 'utils/automationUtil'
import { formatNumber, formatPercent } from '../utils/format'
import { ChartValue, calPercent, calcTotal } from '../utils/chartValue'
import { Explanation } from './Explanation'
import { ExplanationData } from '../../../types'
import Text from './Text'

const LegendContainer = styled.div<{ columnCounts: number }>`
  width: 100%;
  max-width: 100%;
  display: grid;
  grid-template-columns: auto ${({ columnCounts }) => 'max-content '.repeat(columnCounts)};
  column-gap: 12px;
  row-gap: 2px;

  font-size: 14px;
  line-height: 22px;
  color: ${({ color }) => color};
`

const Name = styled.div.attrs({
  [AUTO_ATTR_KEY]: AutomationId.metricGrid.legend.name
})<{ fontWeight: string }>`
  display: flex;
  gap: 8px;
  align-items: center;
  text-align: left;
  font-weight: ${({ fontWeight }) => fontWeight};
  overflow: hidden;
`

const TextContainer = styled.div`
  flex-shrink: 1;
  overflow: hidden;
`

const Value = styled.div.attrs({
  [AUTO_ATTR_KEY]: AutomationId.metricGrid.legend.value
})<{ fontWeight: string }>`
  text-align: right;
  font-weight: ${({ fontWeight }) => fontWeight};
`
const Percentage = styled.div.attrs({
  [AUTO_ATTR_KEY]: AutomationId.metricGrid.legend.percentage
})<{ fontWeight: string }>`
  text-align: right;
  font-weight: ${({ fontWeight }) => fontWeight};
`

const SquareSymbol = styled.div`
  width: 12px;
  height: 12px;
  background: ${({ color }) => color};
  border-radius: 2px;
  flex-shrink: 0;
`

const ShiftLeft = css`
  margin-left: -3px;
`

export interface LegendData {
  name: string
  y: ChartValue
  explanation?: ExplanationData
}

export interface LegendProps {
  data: LegendData[]
  colorSettings: Record<string, string>
  isDisplayPercent: boolean
}

export const Legend = ({ data, colorSettings, isDisplayPercent }: LegendProps) => {
  const sysTheme = useSysTheme()
  const total = isDisplayPercent ? calcTotal(data.map((d) => d.y)) : 0

  return (
    <LegendContainer color={sysTheme.text.primary} columnCounts={isDisplayPercent ? 2 : 1}>
      {data.map((d) => (
        <React.Fragment key={d.name}>
          <Name fontWeight={sysTheme.fontWeight.regular}>
            <SquareSymbol color={getDefaultColor(colorSettings, d.name, sysTheme)} />
            <TextContainer>
              <Text>{d.name}</Text>
            </TextContainer>
            {d.explanation && (
              // eslint-disable-next-line react/jsx-props-no-spreading
              <Explanation styles={ShiftLeft} iconSize={14} {...d.explanation} />
            )}
          </Name>
          <Value fontWeight={sysTheme.fontWeight.semibold}>{formatNumber(d.y)}</Value>
          {isDisplayPercent && (
            <Percentage fontWeight={sysTheme.fontWeight.semibold}>
              {formatPercent(calPercent(d.y, total))}
            </Percentage>
          )}
        </React.Fragment>
      ))}
    </LegendContainer>
  )
}
